import LockIcon from '@mui/icons-material/Lock';
import {
  Box,
  Container,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';
import { Heading } from '../../components/Heading';
import {
  CustomizationCategory,
  useCustomization,
} from '../../contexts/customizationContext';

// Replaces a hidden+locked option's label, description and progress.
const OBSCURED_LABEL = 'Dold';

interface CategorySelectProps {
  category: CustomizationCategory;
  label: string;
}

function CategorySelect({ category, label }: CategorySelectProps) {
  const { categories, selection, selectOption } = useCustomization();
  const selectedId = selection[category];

  const effectiveCategory = categories.find(c => c.id === category);

  const handleChange = (event: SelectChangeEvent<string>) => {
    selectOption(category, event.target.value);
  };

  // Without this `renderValue` MUI renders the full MenuItem children in the
  // closed select — including description and lock icon — which makes the
  // input visibly taller than the single-line options. The selected option
  // is always unlocked, so the obscured branch below is just a safety net.
  const renderValue = (value: string) => {
    const option = effectiveCategory?.options.find(o => o.id === value);
    if (option === undefined) return value;
    const obscured = option.hidden === true && option.locked;
    return (
      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="body1" component="span">
          {obscured ? OBSCURED_LABEL : option.label}
        </Typography>
      </Stack>
    );
  };

  const labelId = `customize-${category}-label`;

  return (
    <FormControl fullWidth>
      <InputLabel id={labelId}>{label}</InputLabel>
      <Select<string>
        labelId={labelId}
        label={label}
        value={selectedId}
        onChange={handleChange}
        renderValue={renderValue}
      >
        {effectiveCategory?.options.map(option => {
          // Hidden+locked options keep their slot but everything that would
          // identify them (label, description, progress) is replaced with a
          // generic "Lås upp för att avslöja" hint until the criterion fires.
          const obscured = option.hidden === true && option.locked;
          return (
            <MenuItem
              key={option.id}
              value={option.id}
              disabled={option.locked}
              // Override MUI's disabled-opacity dim so the unlock description
              // sitting underneath stays legible (the colour still conveys
              // the locked state).
              sx={{
                whiteSpace: 'normal',
                '&.Mui-disabled': { opacity: 1 },
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={1.5}
                width="100%"
              >
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Typography
                        variant="body1"
                        color={option.locked ? 'text.disabled' : 'text.primary'}
                      >
                        {obscured ? OBSCURED_LABEL : option.label}
                      </Typography>
                    </Stack>
                  }
                  // component="span" all the way down keeps the secondary
                  // line as inline markup; the default <Typography component
                  // ="p"> would otherwise wrap a <div> below, which is
                  // invalid HTML.
                  secondaryTypographyProps={{ component: 'span' }}
                  secondary={
                    obscured ? (
                      <Stack direction="row" alignItems="center" gap={0.5}>
                        {option.locked && (
                          <LockIcon
                            fontSize="inherit"
                            sx={{ color: 'text.disabled' }}
                          />
                        )}
                        <Typography
                          component="span"
                          variant="caption"
                          color="text.secondary"
                          sx={{ whiteSpace: 'normal' }}
                        >
                          Lås upp för att avslöja
                        </Typography>
                      </Stack>
                    ) : option.description !== undefined ||
                      option.progress !== null ? (
                      <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="flex-start"
                        gap={1}
                      >
                        <Stack direction="row" alignItems="center" gap={0.5}>
                          {option.locked && (
                            <LockIcon
                              fontSize="inherit"
                              sx={{ color: 'text.disabled' }}
                            />
                          )}
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: 'normal' }}
                          >
                            {option.description}
                          </Typography>
                        </Stack>
                        {option.progress !== null && (
                          <Typography
                            component="span"
                            variant="caption"
                            color="text.secondary"
                            sx={{ whiteSpace: 'nowrap', flexShrink: 0 }}
                          >
                            {option.progress.current} / {option.progress.target}
                          </Typography>
                        )}
                      </Stack>
                    ) : undefined
                  }
                />
              </Stack>
            </MenuItem>
          );
        })}
      </Select>
    </FormControl>
  );
}

export default function GameCustomizePage() {
  const { categories } = useCustomization();
  return (
    <Container component="main" maxWidth="sm">
      <Stack gap={4} p={2}>
        <Heading>Anpassa</Heading>

        <Stack gap={3}>
          {categories.map(category => (
            <Box key={category.id}>
              <CategorySelect category={category.id} label={category.label} />
            </Box>
          ))}
        </Stack>
      </Stack>
    </Container>
  );
}
